import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { useSesion } from '../../../common/hooks/useSesion.js';
import { obtenerPerfil, type PerfilDeCuenta } from '../../../common/services/cuentas.js';
import { pedirEstudios, type ConfigEstudios, type EstudioEnviado } from '../../../common/services/estudios.js';
import type { Idioma } from '../../../i18n/index.js';
import { BOTON_ESTUDIOS, CAMPO_ESTUDIOS, ListaEstudios } from '../components/ListaEstudios.js';
import { MisDominios } from '../components/MisDominios.js';
import { textosEstudios } from '../textos.js';

export function MisEstudiosPage({ idioma }: { readonly idioma: Idioma }) {
  const sesion = useSesion();
  if (sesion === null) return <Navigate to='/iniciar-sesion?volver=%2Fmis-estudios' replace />;
  if (sesion === undefined) return <main className='grow p-6'>{textosEstudios(idioma).cargando}</main>;
  return <PanelEstudios key={`${sesion.user.id}:${sesion.access_token}`} token={sesion.access_token} idioma={idioma} />;
}

function PanelEstudios({ token, idioma }: { readonly token: string; readonly idioma: Idioma }) {
  const t = textosEstudios(idioma);
  const [datos, setDatos] = useState<{ perfil: PerfilDeCuenta; config: ConfigEstudios; filas: readonly EstudioEnviado[] } | null>();
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const vigente = useRef(true);
  const peticion = useRef(0);
  const archivo = useRef<HTMLInputElement>(null);
  const refrescar = useCallback(async () => {
    const actual = ++peticion.current;
    const [perfil, config, lista] = await Promise.all([
      obtenerPerfil(token),
      pedirEstudios<ConfigEstudios>(token, '/config'),
      pedirEstudios<{ filas: readonly EstudioEnviado[] }>(token, '/'),
    ]);
    if (vigente.current && actual === peticion.current)
      setDatos(perfil && config.ok && lista.ok ? { perfil, config: config.datos, filas: lista.datos.filas } : null);
  }, [token]);
  useEffect(() => {
    vigente.current = true;
    void refrescar();
    return () => {
      vigente.current = false;
    };
  }, [refrescar]);

  const ejecutar = async (id: string, accion: 'pdf' | 'retirar' | 'aprobar' | 'rechazar') => {
    if (ocupado) return;
    setOcupado(true);
    setMensaje('');
    setPdfUrl(null);
    const r = await pedirEstudios<{ url: string }>(token, `/${encodeURIComponent(id)}/${accion}`, accion === 'pdf' ? 'GET' : 'POST');
    if (!vigente.current) return;
    if (!r.ok) setMensaje(t.error);
    else if (accion === 'pdf') setPdfUrl(r.datos.url);
    else {
      setMensaje(t.guardado);
      await refrescar();
    }
    if (vigente.current) setOcupado(false);
  };

  const enviar = async (formulario: HTMLFormElement) => {
    if (ocupado || !datos) return;
    const pdf = archivo.current?.files?.[0];
    if (!pdf || !pdf.name.toLowerCase().endsWith('.pdf') || pdf.size > datos.config.maxBytes || pdf.size === 0) {
      setMensaje(t.invalido);
      return;
    }
    setOcupado(true);
    setMensaje('');
    const r = await pedirEstudios<EstudioEnviado>(token, '/', 'POST', new FormData(formulario));
    if (!vigente.current) return;
    setMensaje(r.ok ? t.guardado : t.error);
    if (r.ok) formulario.reset();
    await refrescar();
    if (vigente.current) setOcupado(false);
  };

  const limiteAlcanzado = datos && datos.filas.filter(f => f.estado === 'enviado').length >= datos.config.limitePendientes;
  return (
    <main className='mx-auto flex w-full max-w-[980px] grow flex-col gap-6 px-6 py-12 font-(family-name:--text) text-(--ink)'>
      <Link to='/mi-cuenta' className='flex w-fit items-center gap-2 text-(--ink-soft) underline underline-offset-4 hover:text-(--ink)'>
        <span aria-hidden='true'>←</span>
        {t.volverCuenta}
      </Link>
      <h1 className='m-0 font-(family-name:--serif) text-3xl font-bold'>{t.titulo}</h1>
      <button
        className={BOTON_ESTUDIOS}
        disabled={ocupado}
        onClick={() => {
          void refrescar();
        }}
        type='button'
      >
        {t.actualizar}
      </button>
      {mensaje ? <p role='status'>{mensaje}</p> : null}
      {pdfUrl ? (
        <a href={pdfUrl} target='_blank' rel='noopener noreferrer' className={BOTON_ESTUDIOS}>
          {t.pdfListo}
        </a>
      ) : null}
      {datos === undefined ? (
        <p>{t.cargando}</p>
      ) : datos === null ? (
        <p role='alert'>{t.sinAcceso}</p>
      ) : (
        <>
          {!datos.perfil.puedeEnviarEstudios ? (
            <p>{t.prohibido}</p>
          ) : (
            <form
              className='flex flex-col gap-3 rounded-xl border border-(--line-strong) p-5'
              onSubmit={e => {
                e.preventDefault();
                void enviar(e.currentTarget);
              }}
            >
              <h2 className='m-0 text-xl font-semibold'>{t.enviar}</h2>
              <p>{t.privado}</p>
              <p>
                {idioma === 'es'
                  ? `Hasta ${datos.config.limitePendientes} archivos pendientes. PDF de hasta`
                  : `Up to ${datos.config.limitePendientes} pending files. PDF up to`}{' '}
                {Math.floor(datos.config.maxBytes / 1024 / 1024)} MiB.
              </p>
              {limiteAlcanzado ? <p role='alert'>{t.limite}</p> : null}
              <fieldset disabled={ocupado || Boolean(limiteAlcanzado)} className='m-0 flex min-w-0 flex-col gap-3 border-0 p-0'>
                <label htmlFor='estudio-titulo'>{t.tituloCampo}</label>
                <input id='estudio-titulo' className={CAMPO_ESTUDIOS} name='titulo' required maxLength={300} />
                <label htmlFor='estudio-editor'>{t.editor}</label>
                <input id='estudio-editor' className={CAMPO_ESTUDIOS} name='editor' required maxLength={300} />
                <label htmlFor='estudio-url'>{t.url}</label>
                <input id='estudio-url' className={CAMPO_ESTUDIOS} name='url' type='url' required maxLength={2000} />
                <label htmlFor='estudio-pdf'>{t.pdf}</label>
                <input id='estudio-pdf' className='max-w-full' name='pdf' type='file' accept='.pdf,application/pdf' required ref={archivo} />
                <label className='flex items-start gap-3'>
                  <input className='mt-1' name='autorizacion' type='checkbox' value='true' required />
                  <span>{t.autorizacion}</span>
                </label>
                <button className={BOTON_ESTUDIOS} type='submit'>
                  {ocupado ? t.cargando : t.enviar}
                </button>
              </fieldset>
            </form>
          )}
          <section aria-labelledby='estudios-subidos' className='mt-2 flex flex-col gap-4'>
            <h2 id='estudios-subidos' className='m-0 font-(family-name:--serif) text-2xl font-bold'>
              {t.subidos}
            </h2>
            <ListaEstudios
              idioma={idioma}
              filas={datos.filas}
              ocupado={ocupado}
              ejecutar={(id, accion) => {
                void ejecutar(id, accion);
              }}
            />
          </section>
          <MisDominios
            token={token}
            idioma={idioma}
            alPurgar={() => {
              void refrescar();
            }}
          />
        </>
      )}
    </main>
  );
}
